// Enlist lists-page kit — the renderer.
//
// A lists page built from theme sections: Favourites, the shopper's lists,
// Create a list, and a side panel. This file renders all of it. Enlist is
// called only through window.qfl.enlist — data (lists(), items(), settings()),
// actions (add / remove / has with a `list`, createList, swapVariant,
// renameList, archiveList) and one event (qfl:wishlist:changed). The contract
// is apps/quick.fire.listings/docs/THEME_INTEGRATION.md, "Building your own
// lists page".
//
// It works on any theme. The only theme-specific piece is the product card,
// which is snippets/enlist-product-card.liquid — swap that file to use your
// theme's own card; see the contract at the top of it.
//
// THE MODEL. If the shop pins a list (Enlist admin → Lists → "Saving from a
// product page always goes to one list"), that list is Favourites: the pool
// every other list is built from. A list's category links show the pool,
// filtered, never the catalogue; finding something new means going to the
// collection page. With no pinned list, Favourites is everything saved.
//
// Hooks are data-enlist-*. They must NOT be data-qfl-*: the Enlist app embed
// loads its own renderers when it finds data-qfl-carousel / data-qfl-outfits /
// data-qfl-create-outfit on a page, and those would fill this markup too.

(() => {
  if (window.__enlistLists) return;           // loaded by several sections
  window.__enlistLists = true;

  // Keyboard or pointer, whichever was used last: the rows' focus ring shows
  // only for the keyboard (enlist-golf-poser.css), since iOS treats some tap
  // focus as :focus-visible.
  const htmlEl = document.documentElement;
  document.addEventListener('keydown', (e) => { if (e.key === 'Tab' || String(e.key || '').startsWith('Arrow')) htmlEl.classList.add('enlist-keyboard'); }, true);
  document.addEventListener('pointerdown', () => htmlEl.classList.remove('enlist-keyboard'), true);

  const Q = () => (window.qfl && window.qfl.enlist) || null;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
  const routeRoot = () => (window.Shopify?.routes?.root) || '/';
  const gidTail = (gid) => String(gid || '').split('/').pop();
  const variantGidOf = (id) => `gid://shopify/ProductVariant/${gidTail(id)}`;
  const settings = () => { try { return Q()?.settings?.() || {}; } catch { return {}; } };
  const term = () => (settings().list_term || '').trim() || 'list';
  const categories = () => settings().grouping_collections || [];
  // A setting read off whichever kit section carries it, with a fallback.
  const attr = (sel, name, fallback = '') => {
    const v = $(sel)?.getAttribute(name);
    return v && v.trim() ? v : fallback;
  };
  const buttonClass = (sel) => attr(sel, 'data-enlist-button-class', 'enlist-button');
  const fill = (template, category, lower) => String(template)
    .replace(/\[category\]/g, lower ? String(category || '').toLowerCase() : String(category || ''));
  // From the nearest kit section, per card — so a label rewritten after a
  // size change uses the same prefix as the one first rendered.
  const sizePrefix = (el) => {
    const p = (el?.closest('[data-enlist-size-prefix]')?.getAttribute('data-enlist-size-prefix') || '').trim() || 'Size:';
    return `${p} `;
  };

  // Enlist keeps prices in the shop's currency (GBP). A shopper who has
  // switched currency sees the storefront's own prices everywhere else, so a
  // stored price is only shown as it is when no conversion is in play; the
  // totals are otherwise worked out from the storefront (livePrices).
  const inShopCurrency = () => Number(window.Shopify?.currency?.rate || 1) === 1;

  function money(cents) {
    const currency = window.Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency })
        .format((Number(cents) || 0) / 100);
    } catch { return ((Number(cents) || 0) / 100).toFixed(2); }
  }

  // A confirmation in Enlist's own banner — the one it shows on "Removed" —
  // so adding and removing read as the same thing. Falls back to the kit's
  // toast on an Enlist without qfl.enlist.toast().
  function confirmToast(text) {
    try {
      if (typeof Q()?.toast === 'function') { Q().toast({ text, dismissible: false }); return; }
    } catch { /* fall through */ }
    toast(text);
  }
  // "Added to outfit", in the shop's own term.
  const termLower = () => term().toLowerCase();
  const termTitle = () => { const t = term(); return t.charAt(0).toUpperCase() + t.slice(1); };

  // Theme-neutral feedback. Not qfl.enlist.toast(): that is drawn by Enlist and styled
  // by its stylesheet, which a theme running this kit usually switches off.
  let toastTimer = null;
  function toast(text, kind = 'info') {
    let el = $('.enlist-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'enlist-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.dataset.kind = kind;
    el.classList.add('enlist-toast--visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('enlist-toast--visible'), 3200);
  }

  // ── The model ────────────────────────────────────────────────
  //
  // ── Sample content, theme editor only ────────────────────────
  //
  // In the theme editor the person editing has usually saved nothing, and
  // every section hides itself when empty, so the page was blank there. With
  // nothing saved AND the editor's sample products on the page
  // (snippets/enlist-sample-data.liquid, which renders only in design mode),
  // the model is two sample lists and a few loose items instead. Nothing is
  // saved while it shows: see SAMPLE_BLOCKED.
  let sampleMode = false;
  let sampleCache;
  function sampleModel() {
    if (!window.Shopify?.designMode) return null;
    if (sampleCache !== undefined) return sampleCache;
    let products = [];
    try { products = JSON.parse($('script[data-enlist-sample]')?.textContent || '[]'); } catch { /* bad island */ }
    if (!products.length) return (sampleCache = null);
    const now = Date.now();
    const at = (i) => new Date(now - i * 60000).toISOString();
    const item = (p, i, qty = 1) => ({ ...p, qty, added_at: at(i) });
    const list = (n, name, picks, contact) => ({
      share_token: `enlist-sample-${n}`, name, can_edit: true, is_primary: false, created_at: at(n),
      extra_fields: contact ? { contact_name: contact } : {},
      items: picks.map(([i, qty]) => products[i] && item(products[i], i, qty)).filter(Boolean),
    });
    // Named with the shop's own term (Enlist settings): "Sample outfit",
    // "Sample project".
    const lists = [
      list(1, `Sample ${term()}`, [[0, 2], [1, 1], [2, 1]], 'Sample contact'),
      list(2, `Another ${term()}`, [[3, 1], [4, 3]]),
    ].filter((l) => l.items.length);
    return (sampleCache = { lists, items: products.map((p, i) => item(p, i)) });
  }
  const SAMPLE_BLOCKED = [
    '[data-enlist-remove]', '[data-enlist-share]', '[data-enlist-share-all]', '[data-enlist-pick]',
    '[data-enlist-panel-add]', '[data-enlist-add-all]', '[data-enlist-create]', '[data-enlist-list-edit]',
  ].join(',');

  // Rebuilt from the bridge on every render rather than patched: lists() and
  // items() are cheap, and state kept here would be a second copy of the
  // app's that could disagree with it.
  function model() {
    let lists = [];
    let items = [];
    try { lists = Q().lists() || []; items = Q().items() || []; } catch { /* not ready */ }
    const sample = !lists.length && !items.length ? sampleModel() : null;
    sampleMode = !!sample;
    if (sample) ({ lists, items } = sample);
    const snap = new Map(items.map((i) => [i.variant_gid, i]));
    const pool = lists.find((l) => l.is_primary) || null;
    // Newest first; lists older than created_at have none, and go last.
    const others = lists
      .filter((l) => !l.is_primary)
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    // An item with no product_handle is a deleted or unpublished product:
    // render nothing for it rather than a titleless, priceless card.
    const entriesOf = (list) => (list?.items || [])
      .filter((i) => i.variant_gid)
      .map((i) => ({ ...(snap.get(i.variant_gid) || {}), ...i, qty: i.qty || 1 }))
      .filter((e) => e.product_handle);
    const favourites = pool
      ? entriesOf(pool)
      : items.filter((i) => i.product_handle).map((i) => ({ ...i, qty: i.qty || 1 }));
    return { lists, pool, others, entriesOf, favourites };
  }

  // ── Cards ────────────────────────────────────────────────────
  //
  // Each card is sections/enlist-card.liquid, fetched over the Section
  // Rendering API with ?variant= so it shows the saved variant. Three
  // behaviours carried over from Enlist's own renderer, each learned the hard
  // way:
  //   - at most 6 requests at once: storefronts throttle bursts of section
  //     requests, and a page with 30 saved items is a burst;
  //   - one retry after a short backoff, so a transient 503 still renders;
  //   - "gone" kept apart from "failed". A 404, or a good response with no
  //     card in it, means the product left the storefront. A 429 or a dropped
  //     connection is transient, and treating it as gone would empty a
  //     shopper's lists during a traffic spike.
  // Cached per product AND variant: the same product saved in two sizes is
  // two cards. Also cached per tab, so a return visit is instant.
  const CONCURRENCY = 6;
  let inFlight = 0;
  const waiting = [];
  const acquire = () => (inFlight < CONCURRENCY ? (inFlight += 1, Promise.resolve()) : new Promise((r) => waiting.push(r)));
  const release = () => { const next = waiting.shift(); if (next) next(); else inFlight -= 1; };

  const SS = 'enlist:card:';
  const ssGet = (k) => { try { return sessionStorage.getItem(SS + k); } catch { return null; } };
  const ssSet = (k, html) => {
    try { sessionStorage.setItem(SS + k, html); } catch {
      // Quota, most likely. Drop only this kit's keys; the theme uses it too.
      try {
        const mine = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(SS)) mine.push(key);
        }
        mine.forEach((key) => sessionStorage.removeItem(key));
      } catch { /* nothing more to try */ }
    }
  };

  const cardCache = new Map();
  const gone = new Set();
  function cardHtml(handle, variantGid) {
    if (!handle) return Promise.resolve(null);
    const id = gidTail(variantGid);
    // Per currency and country too: the card carries a price, and the tab
    // outlives a currency switch.
    const key = `${window.Shopify?.currency?.active || ''}:${window.Shopify?.country || ''}::${handle}::${id}`;
    if (cardCache.has(key)) return cardCache.get(key);
    const cached = ssGet(key);
    if (cached) { const hit = Promise.resolve(cached); cardCache.set(key, hit); return hit; }
    const once = async () => {
      await acquire();
      let res;
      try {
        res = await fetch(`${routeRoot()}products/${encodeURIComponent(handle)}?section_id=enlist-card${id ? `&variant=${encodeURIComponent(id)}` : ''}`);
      } finally { release(); }
      if (res.status === 404) { gone.add(handle); return null; }
      if (!res.ok) return null;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const host = doc.querySelector('[data-enlist-card-host]');
      if (host && host.querySelector('[data-enlist-card]')) {
        gone.delete(handle);
        ssSet(key, host.outerHTML);
        return host.outerHTML;
      }
      gone.add(handle);
      return null;
    };
    const p = (async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try { const html = await once(); if (html) return html; } catch { /* retry */ }
        if (gone.has(handle)) break;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 400 + Math.floor(Math.random() * 300)));
      }
      cardCache.delete(key);          // a miss is not cached: the next render retries
      return null;
    })();
    cardCache.set(key, p);
    return p;
  }

  // When the card could not be fetched this time (throttled, offline) — never
  // for a product that is gone. Enough to recognise it and reach it.
  function fallbackCard(e) {
    const url = e.product_url || `${routeRoot()}products/${encodeURIComponent(e.product_handle)}`;
    return `<div class="enlist-card" data-enlist-card>
      <div class="enlist-card__media"><a href="${esc(url)}">${e.image_url ? `<img class="enlist-card__img" src="${esc(e.image_url)}" alt="" loading="lazy">` : ''}</a>
        <div class="enlist-card__actions" data-enlist-actions></div></div>
      <div class="enlist-card__info"><a class="enlist-card__title" href="${esc(url)}">${esc(e.product_title || '')}</a>
        ${e.price_cents != null && inShopCurrency() ? `<div class="enlist-card__price"><span>${esc(money(e.price_cents))}</span></div>` : ''}</div>
    </div>`;
  }

  const skeletons = (n) => Array.from({ length: n }, () => `<li class="enlist-skeleton" aria-hidden="true">
      <div class="enlist-skeleton__media"></div><div class="enlist-skeleton__line"></div>
      <div class="enlist-skeleton__line enlist-skeleton__line--short"></div></li>`).join('');

  // Where the kit's buttons go inside a card: a [data-enlist-actions] slot, or
  // the element a card names with data-enlist-actions-selector, or the card.
  function actionsSlot(li) {
    const card = $('[data-enlist-card]', li);
    if (!card) return li;
    const named = card.getAttribute('data-enlist-actions-selector');
    if (named) { try { const el = card.querySelector(named); if (el) return el; } catch { /* bad selector */ } }
    return $('[data-enlist-actions]', card) || card;
  }

  // ── Keyed card lists ─────────────────────────────────────────
  //
  // A card is kept, keyed by its variant, for as long as it is in the list, and
  // only moved when the order changes. Rebuilding cards on every change would
  // re-fetch or re-initialise the theme's card each time, and flash.
  const renderSeq = new WeakMap();
  async function renderCards(ul, entries, decorate) {
    if (!ul) return;
    const seq = (renderSeq.get(ul) || 0) + 1;
    renderSeq.set(ul, seq);
    const existing = new Map($$(':scope > li[data-variant-gid]', ul).map((li) => [li.dataset.variantGid, li]));
    if (!existing.size && entries.length && !ul.dataset.enlistPainted) ul.innerHTML = skeletons(entries.length);

    const htmls = await Promise.all(entries.map((e) => (existing.has(e.variant_gid) ? null : cardHtml(e.product_handle, e.variant_gid))));
    if (renderSeq.get(ul) !== seq) return;   // a newer render has started; let it win

    const nodes = [];
    entries.forEach((e, i) => {
      let li = existing.get(e.variant_gid);
      if (!li) {
        const html = htmls[i];
        if (!html && gone.has(e.product_handle)) return;
        li = document.createElement('li');
        li.className = 'enlist-item';
        li.dataset.variantGid = e.variant_gid;
        if (e.product_gid) li.dataset.productGid = e.product_gid;
        li.innerHTML = html || fallbackCard(e);
        decorate(li, e);
        if (e.variant_chosen !== false) preselectSize(li);
      }
      li.toggleAttribute('data-enlist-size-unchosen', e.variant_chosen === false);
      nodes.push(li);
    });
    const same = nodes.length === ul.children.length && nodes.every((n, i) => ul.children[i] === n);
    if (!same) {
      // Keep the row where the shopper swiped it: re-inserting a scroller's
      // children can drop it back to the start (Golf Poser, on a phone).
      const scroller = ul.closest('.scroll-slider') || ul;
      const x = scroller.scrollLeft;
      ul.replaceChildren(...nodes);
      if (x && scroller.scrollLeft !== x) scroller.scrollLeft = x;
    }
    ul.dataset.enlistPainted = '1';
    markSizes(ul);
  }

  // A new card opens with its saved size SELECTED, not just marked, so the
  // card's own button is "Add to cart" rather than "Quick view". Done before
  // the card is in the page: the theme's card reads its selected size once,
  // when it connects. Uses the card's own size buttons and value attribute
  // (the snippet contract), and adds the theme's own `selected` class.
  function preselectSize(li) {
    const card = $('[data-enlist-card]', li);
    const btnSel = card?.getAttribute('data-enlist-size-button');
    const valAttr = card?.getAttribute('data-enlist-size-value-attr');
    if (!btnSel || !valAttr) return;
    let sizes = {};
    try { sizes = JSON.parse($('[data-enlist-variant-sizes]', li)?.textContent || '{}'); } catch { return; }
    const size = sizes[gidTail(li.dataset.variantGid)];
    if (!size) return;
    try {
      const btn = $$(btnSel, card).find((b) => b.getAttribute(valAttr) === size);
      if (!btn || btn.classList.contains('out-of-stock')) return;
      btn.classList.add('selected');
    } catch { return; /* bad selector */ }
    // The theme's card sets its button up once without a variant, which with
    // a size selected reads "Sold out". Once it has booted, hand it the
    // saved variant so the button says "Add to cart".
    let tries = 0;
    const settle = () => {
      const pc = $('product-card', li);
      if (!pc || !pc.isInitialized) { if ((tries += 1) < 20) setTimeout(settle, 50); return; }
      const v = (pc.variants || []).find((x) => String(x.id) === gidTail(li.dataset.variantGid));
      if (v && typeof pc.updateActionButton === 'function') pc.updateActionButton(v);
    };
    setTimeout(settle, 0);
  }

  // ── "Size: 32R" ──────────────────────────────────────────────
  //
  // The size of the SAVED variant, from the sizes enlist-card.liquid emits
  // beside every card. Written wherever the card asks for it (see the contract
  // in snippets/enlist-product-card.liquid), and re-written after a shopper
  // picks a different size, because the card is kept rather than refetched.
  function markSizes(scope) {
    $$('li.enlist-item[data-variant-gid]', scope).forEach((li) => {
      const prefix = sizePrefix(li);
      const card = $('[data-enlist-card]', li);
      if (!card) return;
      let sizes = {};
      try { sizes = JSON.parse($('[data-enlist-variant-sizes]', li)?.textContent || '{}'); } catch { /* none */ }
      // Saved without choosing a size: show none, rather than the variant
      // Enlist had to store (the product's first), which the shopper never picked.
      const size = li.hasAttribute('data-enlist-size-unchosen') ? null : (sizes[gidTail(li.dataset.variantGid)] || null);

      // Highlight among the card's own size buttons, if it names them.
      const btnSel = card.getAttribute('data-enlist-size-button');
      const valAttr = card.getAttribute('data-enlist-size-value-attr');
      if (btnSel && valAttr) {
        try {
          $$(btnSel, card).forEach((b) => b.classList.toggle('enlist-size--saved', !!size && b.getAttribute(valAttr) === size));
        } catch { /* bad selector */ }
      }

      const text = size ? `${prefix}${size}` : '';
      let label = $('[data-enlist-size-label]', card);
      if (!label) {
        const after = card.getAttribute('data-enlist-size-after');
        let anchor = null;
        if (after) { try { anchor = card.querySelector(after); } catch { /* bad selector */ } }
        if (!anchor) return;
        label = anchor.parentElement?.querySelector(':scope > [data-enlist-size-label]');
        if (!label) {
          anchor.insertAdjacentHTML('afterend', '<p class="enlist-card__size" data-enlist-size-label></p>');
          label = anchor.nextElementSibling;
        }
      }
      if (label.textContent !== text) label.textContent = text;
      label.hidden = !text;
    });
  }

  // ── Favourites ───────────────────────────────────────────────
  const FAV = '[data-enlist-favourites]';
  let favSort = 'newness';

  function sortEntries(entries, sort) {
    const out = [...entries];
    if (sort === 'title') out.sort((a, b) => String(a.product_title || '').localeCompare(String(b.product_title || '')));
    else if (sort === 'price') out.sort((a, b) => (a.price_cents || 0) - (b.price_cents || 0));
    else out.sort((a, b) => String(b.added_at || '').localeCompare(String(a.added_at || '')));   // undated last
    return out;
  }

  // A link per category, "Trousers (4) +", to that collection page. A plus
  // means "go and get more", so only the links that leave carry one. Never
  // disabled: a category with nothing saved is when a shopper wants to go
  // and find some.
  function renderCategoryLinks(root, entries) {
    const host = $('[data-enlist-categories]', root);
    if (!host) return;
    const cats = categories();
    host.hidden = !cats.length;
    const html = cats.map((c) => {
      const n = entries.filter((e) => (e.collection_handles || []).includes(c.handle)).length;
      return `<a class="enlist-cat" href="${esc(routeRoot())}collections/${esc(c.handle)}">`
        + `<span class="enlist-cat__label">${esc(c.title || c.handle)} (${n})</span>`
        + '<span class="enlist-cat__plus" aria-hidden="true">+</span></a>';
    }).join('');
    if (host.innerHTML !== html) host.innerHTML = html;
  }

  async function renderFavourites(m) {
    const root = $(FAV);
    if (!root) return;
    const entries = sortEntries(m.favourites, favSort);
    root.hidden = entries.length === 0;
    root.dataset.enlistList = m.pool?.share_token || '';
    renderCategoryLinks(root, entries);
    const countEl = $('[data-enlist-count]', root);
    if (countEl) {
      const n = entries.length;
      countEl.textContent = `${n} ${n === 1 ? attr(FAV, 'data-enlist-count-one', 'item') : attr(FAV, 'data-enlist-count-other', 'items')}`;
    }
    const addLabel = attr(FAV, 'data-enlist-add-label', `Add to ${term()}`);
    const cls = buttonClass(FAV);
    await renderCards($('[data-enlist-track]', root), entries, (li) => {
      li.insertAdjacentHTML('beforeend', '<button type="button" class="enlist-remove" data-enlist-remove aria-label="Remove">✕</button>');
      actionsSlot(li).insertAdjacentHTML('beforeend',
        `<button type="button" class="${esc(cls)} enlist-pick" data-enlist-pick>${esc(addLabel)}</button>`);
    });
  }

  // ── The shopper's lists ──────────────────────────────────────
  const LISTS = '[data-enlist-lists]';
  const rows = new Map();

  function renderListLinks(host) {
    if (!host) return;
    const cats = categories();
    host.hidden = !cats.length;
    // The category PICKER: opens the pool, filtered. No count and no plus —
    // those belong to the links that leave for the catalogue.
    const html = cats.map((c) => `<a class="enlist-chip" data-enlist-bubble="${esc(c.handle)}"`
      + ` data-enlist-title="${esc(c.title || c.handle)}" href="${esc(routeRoot())}collections/${esc(c.handle)}">`
      + `${esc(c.title || c.handle)}</a>`).join('');
    if (host.innerHTML !== html) host.innerHTML = html;
  }

  function totals(entries) {
    let total = 0;
    let compare = 0;
    for (const e of entries) {
      const qty = e.qty || 1;
      if (e.price_cents != null) total += e.price_cents * qty;
      compare += (e.compare_at_price_cents != null ? e.compare_at_price_cents : (e.price_cents || 0)) * qty;
    }
    return { total, compare: compare > total ? compare : null };
  }

  // Prices in the shopper's currency, from the storefront's product JSON
  // (cached per product): variant_gid → { price_cents, compare_at_price_cents }.
  async function livePrices(entries) {
    const out = new Map();
    await Promise.all(entries.map(async (e) => {
      const prod = e.product_handle ? await productJson(e.product_handle) : null;
      const v = (prod?.variants || []).find((x) => variantGidOf(x.id) === e.variant_gid);
      if (v) out.set(e.variant_gid, { price_cents: v.price, compare_at_price_cents: v.compare_at_price || null });
    }));
    return out;
  }

  async function renderLists(m) {
    const root = $(LISTS);
    if (!root) return;
    const host = $('[data-enlist-lists-host]', root) || root;
    const tpl = $('template[data-enlist-list-template]', root);
    if (!tpl) return;
    // Every list except the pinned one. With nothing pinned that is every
    // list, and Favourites above is everything saved across them.
    const lists = m.others;
    root.hidden = lists.length === 0;

    const work = [];
    for (const l of lists) {
      let row = rows.get(l.share_token);
      if (!row) {
        row = document.createElement('div');
        row.className = 'enlist-list';
        row.setAttribute('data-enlist-list-row', '');
        row.dataset.enlistList = l.share_token;
        row.append(tpl.content.cloneNode(true));
        rows.set(l.share_token, row);
      }
      const entries = m.entriesOf(l);
      const set = (sel, text) => { const el = $(sel, row); if (el && el.textContent !== text) el.textContent = text; };
      const paintTotals = ({ total, compare }) => {
        set('[data-enlist-total]', total ? money(total) : '');
        set('[data-enlist-compare]', compare ? money(compare) : '');
      };
      // Stored prices at once when they are in the shopper's currency; then
      // the storefront's, which are right in any currency. The newest render
      // of a row wins.
      if (inShopCurrency()) paintTotals(totals(entries));
      const seq = (row._enlistTotalsSeq = (row._enlistTotalsSeq || 0) + 1);
      livePrices(entries).then((live) => {
        if (row._enlistTotalsSeq !== seq) return;
        paintTotals(totals(entries.map((e) => ({ ...e, ...(live.get(e.variant_gid) || (inShopCurrency() ? {} : { price_cents: null, compare_at_price_cents: null })) }))));
      });
      set('[data-enlist-name]', (l.name || '').trim() || `My ${term()}`);
      set('[data-enlist-count]', String(entries.length));
      row.classList.toggle('enlist-list--empty', entries.length === 0);
      const edit = $('[data-enlist-list-edit]', row);
      if (edit) edit.hidden = !l.can_edit;
      renderListLinks($('[data-enlist-bubbles]', row));
      work.push({ row, entries });
    }
    for (const token of [...rows.keys()]) {
      if (!lists.some((l) => l.share_token === token)) rows.delete(token);
    }
    const want = work.map((w) => w.row);
    const same = want.length === host.children.length && want.every((n, i) => host.children[i] === n);
    if (!same) host.replaceChildren(...want);

    await Promise.all(work.map(({ row, entries }) => renderCards($('[data-enlist-items]', row), entries, (li) => {
      li.insertAdjacentHTML('beforeend', '<button type="button" class="enlist-remove" data-enlist-remove aria-label="Remove">✕</button>');
    })));
  }

  // ── The side panel: the pool, one category ──────────────────
  const PANEL = '[data-enlist-panel]';
  let panelState = null;   // { handle, title, list }

  function openPanel() {
    const el = $(PANEL);
    if (!el) return false;
    el.classList.add('enlist-panel--open');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('enlist-no-scroll');
    return true;
  }
  function closePanel() {
    const el = $(PANEL);
    if (!el) return;
    el.classList.remove('enlist-panel--open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('enlist-no-scroll');
    const body = $('[data-enlist-panel-body]', el);
    if (body) body.replaceChildren();
    panelState = null;
  }

  async function renderPanel() {
    const el = $(PANEL);
    const body = el && $('[data-enlist-panel-body]', el);
    if (!body || !panelState) return;
    const { handle, title, list } = panelState;
    const m = model();
    const entries = m.favourites.filter((e) => (e.collection_handles || []).includes(handle));
    const cls = buttonClass(PANEL);

    // A prompt, not a dead end: say so, and link to the collection.
    if (!entries.length) {
      body.innerHTML = '<div class="enlist-panel__empty">'
        + `<p>${esc(fill(attr(PANEL, 'data-enlist-empty-text', "You haven't saved any [category] yet."), title, true))}</p>`
        + `<a class="${esc(cls)}" href="${esc(routeRoot())}collections/${esc(handle)}">${esc(fill(attr(PANEL, 'data-enlist-browse-label', 'Browse [category]'), title, false))}</a>`
        + '</div>';
      return;
    }
    let ul = $('ul.enlist-panel__grid', body);
    if (!ul || ul.dataset.enlistTarget !== list) {
      body.innerHTML = `<ul class="enlist-panel__grid" data-enlist-target="${esc(list)}" data-enlist-list="${esc(m.pool?.share_token || '')}"></ul>`;
      ul = $('ul.enlist-panel__grid', body);
    }
    await renderCards(ul, entries, (li) => {
      // In the card's own button row, beside QUICK VIEW, as on a Favourites
      // card (actionsSlot), rather than under the card.
      actionsSlot(li).insertAdjacentHTML('beforeend', `<button type="button" class="${esc(cls)} enlist-panel__add" data-enlist-panel-add></button>`);
    });
    syncPanelButtons();
  }

  // Which of these the list already holds — from state, every time, so it is
  // right after an add, after a removal behind the panel, and on reopening.
  function syncPanelButtons() {
    const ul = $('ul.enlist-panel__grid');
    if (!ul) return;
    const list = ul.dataset.enlistTarget;
    const addLabel = attr(PANEL, 'data-enlist-add-label', `Add to ${term()}`);
    const addedLabel = attr(PANEL, 'data-enlist-added-label', 'Added');
    $$('li.enlist-item', ul).forEach((li) => {
      const btn = $('[data-enlist-panel-add]', li);
      if (!btn || btn.dataset.enlistBusy === '1') return;
      let held = false;
      try { held = !!Q().has({ variantGid: li.dataset.variantGid, list }); } catch { /* not ready */ }
      btn.textContent = held ? addedLabel : addLabel;
      btn.classList.toggle('enlist-panel__add--added', held);
      btn.disabled = held;
    });
  }

  async function openCategory(handle, title, list) {
    if (!openPanel()) return false;
    const el = $(PANEL);
    const titleEl = $('[data-enlist-panel-title]', el);
    if (titleEl) titleEl.textContent = title || '';
    const body = $('[data-enlist-panel-body]', el);
    if (body) body.replaceChildren();
    panelState = { handle, title, list };
    await renderPanel();
    return true;
  }

  // The picker covers what a card names with data-enlist-picker-over (its
  // picture, say), measured, so it fits exactly whatever the card's shape;
  // without one, the whole card (CSS inset: 0). Measured rather than left to
  // CSS: iOS Safari sized a picker given aspect-ratio and left/right to its
  // own text, so it covered part of the picture (Golf Poser, 2026-09-27).
  function placeOver(li, overlay) {
    const card = $('[data-enlist-card]', li);
    const sel = card?.getAttribute('data-enlist-picker-over');
    let target = null;
    if (sel) { try { target = card.querySelector(sel); } catch { /* bad selector */ } }
    if (!target) return;
    const a = li.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    if (!b.width || !b.height) return;
    Object.assign(overlay.style, {
      top: `${b.top - a.top}px`, left: `${b.left - a.left}px`,
      width: `${b.width}px`, height: `${b.height}px`,
      right: 'auto', bottom: 'auto', aspectRatio: 'auto',
    });
  }

  // ── "Add to list" from a Favourites card ─────────────────────
  //
  // An overlay on the card listing the shopper's lists — never the pinned one,
  // where a checkbox could drop an item from the pool while leaving it in the
  // lists — with a way to start a new list.
  //
  // Focus moves into it only when it was opened from the keyboard, and goes
  // back to the button that opened it. Opened by a tap, focus is left alone
  // and dropped on close: removing a focused overlay made iOS Safari move
  // focus to the row's scroll slider and draw its keyboard ring (a black box
  // round the whole row) after "Add to outfit" was closed. Golf Poser,
  // 2026-09-27.
  function openPicker(li, opener = null, byKeyboard = false) {
    const variantGid = li?.dataset.variantGid;
    if (!variantGid) return;
    const productGid = li.dataset.productGid || undefined;
    const variantChosen = !li.hasAttribute('data-enlist-size-unchosen');
    $('.enlist-picker', li)?.remove();
    const t = term();
    const article = /^[aeiou]/i.test(t) ? 'an' : 'a';
    const m = model();
    const rowsData = m.others.map((l) => ({
      list: l.share_token,
      name: (l.name || '').trim() || `My ${t}`,
      initial: !!Q().has({ variantGid, list: l.share_token }),
    }));
    // In none of them yet: the newest list (m.others is newest first) comes
    // ticked, so one tap on Done adds it there. Already in some: exactly
    // those are ticked, as before. Golf Poser, 2026-09-26.
    const suggest = rowsData.length && !rowsData.some((r) => r.initial) ? 0 : -1;
    const rowHtml = (r, i) => `
      <label class="enlist-picker__option">
        <input type="checkbox" data-enlist-pick-row="${i}" ${r.initial || i === suggest ? 'checked' : ''}>
        <span>${esc(r.name)}</span>
      </label>`;
    const overlay = document.createElement('div');
    overlay.className = 'enlist-picker';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', `Add to ${article} ${t}`);
    overlay.innerHTML = `
      <button type="button" class="enlist-picker__close" data-enlist-pick-close aria-label="Close">✕</button>
      <p class="enlist-picker__title">Add to ${article} ${esc(t)}</p>
      <div class="enlist-picker__options" data-enlist-pick-options>${rowsData.length
    ? rowsData.map(rowHtml).join('')
    : `<p class="enlist-picker__empty">No ${esc(t.toLowerCase())}s yet. Create one below.</p>`}</div>
      <button type="button" class="enlist-link-button" data-enlist-pick-create>+ New ${esc(t)}</button>
      <button type="button" class="${esc(buttonClass(FAV))}" data-enlist-pick-confirm>Done</button>`;
    li.appendChild(overlay);
    placeOver(li, overlay);
    li.classList.add('enlist-item--picking');
    if (byKeyboard) $('input, button', overlay)?.focus();

    const close = () => {
      const hadFocus = overlay.contains(document.activeElement);
      if (hadFocus) document.activeElement.blur();
      overlay.remove();
      if (hadFocus && byKeyboard && opener && document.contains(opener)) opener.focus({ preventScroll: true });
      li.classList.remove('enlist-item--picking');
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('click', onOutside, true);
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    // Not a tap in "Choose a size", which Done opens on top: closing the
    // picker behind it on the first size tapped made the page jump under the
    // dialog. Done closes the picker once the size is confirmed.
    const onOutside = (e) => {
      if (overlay.contains(e.target) || e.target?.closest?.('[data-enlist-size-dialog]')) return;
      close();
    };
    document.addEventListener('keydown', onKey, true);
    setTimeout(() => document.addEventListener('click', onOutside, true), 0);

    overlay.addEventListener('click', async (e) => {
      if (e.target.closest('[data-enlist-pick-close]')) { close(); return; }
      const create = e.target.closest('[data-enlist-pick-create]');
      if (create) {
        create.disabled = true;
        const r = await Q().createList(`New ${t}`);
        create.disabled = false;
        if (!r?.ok || !r.share_token) { toast('Something went wrong.', 'error'); return; }
        rowsData.push({ list: r.share_token, name: `New ${t}`, initial: false });
        const i = rowsData.length - 1;
        const box = $('[data-enlist-pick-options]', overlay);
        $('.enlist-picker__empty', box)?.remove();
        box.insertAdjacentHTML('beforeend', rowHtml(rowsData[i], i));
        $(`[data-enlist-pick-row="${i}"]`, box).checked = true;
        return;
      }
      const confirm = e.target.closest('[data-enlist-pick-confirm]');
      if (confirm) {
        confirm.disabled = true;
        // Adding somewhere, and no size chosen yet: ask for one first.
        let addGid = variantGid;
        let addChosen = variantChosen;
        const adding = rowsData.some((r, i) => !!$(`[data-enlist-pick-row="${i}"]`, overlay)?.checked && !r.initial);
        if (adding && !variantChosen) {
          const e = entryFor(variantGid);
          const chosen = e ? await chooseSizes([{ ...e, variant_chosen: false }]) : new Map();
          if (!chosen) { confirm.disabled = false; return; }
          if (chosen.has(variantGid)) {
            addGid = chosen.get(variantGid);
            addChosen = true;
            await saveChosenSizes(chosen);
          }
        }
        let added = 0;
        let failed = false;
        for (let i = 0; i < rowsData.length; i += 1) {
          const want = !!$(`[data-enlist-pick-row="${i}"]`, overlay)?.checked;
          if (want === rowsData[i].initial) continue;
          if (want) {
            // eslint-disable-next-line no-await-in-loop
            const r = await Q().add({ variantGid: addGid, productGid, list: rowsData[i].list, variantChosen: addChosen });
            if (r?.ok) added += 1; else failed = true;
          } else {
            // eslint-disable-next-line no-await-in-loop
            await Q().remove({ variantGid: addGid, list: rowsData[i].list });
          }
        }
        close();
        if (failed) toast('Something went wrong.', 'error');
        else if (added) confirmToast(`Added to ${termLower()}`);
      }
    });
  }

  // ── Rename / delete a list ───────────────────────────────────
  function openEdit(row) {
    const dialog = $('[data-enlist-edit-dialog]');
    const list = row?.dataset.enlistList;
    if (!dialog || !list || typeof dialog.showModal !== 'function') return;
    const l = model().lists.find((x) => x.share_token === list);
    if (!l) return;
    dialog.dataset.enlistList = list;
    const input = $('[data-enlist-edit-name]', dialog);
    input.value = (l.name || '').trim();
    $('[data-enlist-edit-error]', dialog).hidden = true;
    $('[data-enlist-edit-confirm]', dialog).hidden = true;
    dialog.showModal();
    // Cursor at the end, nothing selected: selecting the whole name invites
    // a stray keystroke to wipe it.
    input.focus();
    try { input.setSelectionRange(input.value.length, input.value.length); } catch { /* not a text input */ }
  }

  async function saveEdit(dialog) {
    const list = dialog.dataset.enlistList;
    const input = $('[data-enlist-edit-name]', dialog);
    const name = (input.value || '').trim();
    const err = $('[data-enlist-edit-error]', dialog);
    if (!name) { err.textContent = 'Give it a name.'; err.hidden = false; return; }
    const r = await Q().renameList(list, name);
    if (r?.ok) dialog.close();
    else { err.textContent = r?.error || 'Something went wrong.'; err.hidden = false; }
  }

  // Deleting takes a whole row out from under the shopper, so the page goes
  // back to the top and says what happened. Golf Poser, 2026-09-26.
  async function deleteList(dialog) {
    const r = await Q().archiveList(dialog.dataset.enlistList);
    if (r?.ok) {
      dialog.close();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      confirmToast(`${termTitle()} deleted`);
    } else {
      const err = $('[data-enlist-edit-error]', dialog);
      err.textContent = r?.error || 'Something went wrong.';
      err.hidden = false;
    }
  }


  // ── "Choose a size" ──────────────────────────────────────────
  //
  // An item saved without a size (variant_chosen: false — a card's save
  // button pressed before any size) cannot go into an outfit or the cart as
  // it is: its stored variant is only the product's first, not the shopper's
  // pick. So before either, a dialog asks for a size for every such product
  // with more than one variant, in one go (Add all to cart can need several).
  // The answers are saved to the lists (swapVariant, which settles the flag
  // everywhere the item is) and used for the add. Cancel adds nothing.
  //
  // Words come from the lists/favourites section: data-enlist-size-title,
  // -size-intro, -size-confirm, -size-cancel, -size-sold-out.
  const productCache = new Map();
  async function productJson(handle) {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(`${routeRoot()}products/${encodeURIComponent(handle)}.js`)
        .then((r) => (r.ok ? r.json() : null)).catch(() => null));
    }
    return productCache.get(handle);
  }
  const words = (name, fallback) => attr(LISTS, `data-enlist-size-${name}`, '') || attr(FAV, `data-enlist-size-${name}`, fallback);

  // entries: [{ variant_gid, product_handle, product_title, variant_chosen }]
  // → Map(old variant_gid → chosen variant_gid), or null if cancelled.
  async function chooseSizes(entries) {
    const chosen = new Map();
    const asks = [];
    for (const e of entries) {
      if (e.variant_chosen !== false || !e.product_handle) continue;
      // eslint-disable-next-line no-await-in-loop
      const p = await productJson(e.product_handle);
      if (!p || !Array.isArray(p.variants) || p.variants.length < 2) continue;
      const sizeIdx = (p.options || []).findIndex((o) => /size|waist|length|fit/i.test(typeof o === 'string' ? o : o?.name || ''));
      asks.push({
        from: e.variant_gid,
        title: e.product_title || p.title,
        options: p.variants.map((v) => ({
          gid: variantGidOf(v.id),
          label: sizeIdx >= 0 ? v.options[sizeIdx] : v.title,
          available: v.available !== false,
        })),
      });
    }
    if (!asks.length) return chosen;

    let dialog = document.querySelector('[data-enlist-size-dialog]');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'enlist-dialog enlist-size-dialog';
      dialog.setAttribute('data-enlist-size-dialog', '');
      document.body.appendChild(dialog);
    }
    const saveCls = document.querySelector('[data-enlist-edit-save]')?.className || buttonClass(LISTS);
    dialog.innerHTML = `
      <form method="dialog" class="enlist-dialog__body">
        <p class="enlist-dialog__label enlist-size-dialog__title">${esc(words('title', 'Choose a size'))}</p>
        <p class="enlist-size-dialog__intro">${esc(words('intro', asks.length > 1 ? 'Pick a size for each of these first.' : 'Pick a size for this first.'))}</p>
        ${asks.map((a, i) => `
          <fieldset class="enlist-size-dialog__product" data-enlist-size-ask="${i}">
            <legend class="enlist-size-dialog__name">${esc(a.title)}</legend>
            <div class="enlist-size-dialog__options">
              ${a.options.map((o) => `<button type="button" class="enlist-size-choice" data-enlist-size-choice="${esc(o.gid)}"${o.available ? '' : ` disabled title="${esc(words('sold-out', 'Sold out'))}"`}>${esc(o.label)}</button>`).join('')}
            </div>
          </fieldset>`).join('')}
        <div class="enlist-dialog__actions">
          <button type="button" class="enlist-link-button" data-enlist-size-cancel>${esc(words('cancel', 'Cancel'))}</button>
          <button type="submit" class="${esc(saveCls)}" data-enlist-size-confirm disabled>${esc(words('confirm', 'Confirm'))}</button>
        </div>
      </form>`;
    const confirmBtn = $('[data-enlist-size-confirm]', dialog);
    const picks = new Array(asks.length).fill(null);
    return new Promise((resolve) => {
      const done = (result) => {
        dialog.removeEventListener('click', onClick);
        dialog.removeEventListener('close', onClose);
        if (dialog.open) dialog.close();
        resolve(result);
      };
      const onClick = (ev) => {
        const t = ev.target instanceof Element ? ev.target : null;
        if (!t) return;
        const choice = t.closest('[data-enlist-size-choice]');
        if (choice && !choice.disabled) {
          const box = choice.closest('[data-enlist-size-ask]');
          $$('[data-enlist-size-choice]', box).forEach((b) => b.classList.toggle('enlist-size-choice--selected', b === choice));
          picks[Number(box.dataset.enlistSizeAsk)] = choice.dataset.enlistSizeChoice;
          confirmBtn.disabled = picks.some((p) => !p);
          return;
        }
        if (t.closest('[data-enlist-size-cancel]')) { ev.preventDefault(); done(null); return; }
        if (t.closest('[data-enlist-size-confirm]')) {
          ev.preventDefault();
          if (picks.some((p) => !p)) return;
          asks.forEach((a, i) => chosen.set(a.from, picks[i]));
          done(chosen);
        }
      };
      const onClose = () => done(null);   // Escape
      dialog.addEventListener('click', onClick);
      dialog.addEventListener('close', onClose);
      dialog.showModal();
      relayout(dialog);
    });
  }

  // iOS Safari opened "Choose a size" with an empty band, about one row of
  // size buttons tall, between the sizes and the buttons, and dropped it the
  // moment a size was tapped (Golf Poser, 2026-09-27; not reproducible in
  // Playwright WebKit). The likeliest cause is the size row's wrapping worked
  // out before the site font arrived and not revisited. Lay the rows out
  // again, now and once the fonts are in, which is all the tap was doing.
  function relayout(dialog) {
    const again = () => {
      if (!dialog.open) return;
      $$('.enlist-size-dialog__options', dialog).forEach((el) => {
        el.style.display = 'none';
        void el.offsetHeight;
        el.style.display = '';
      });
    };
    requestAnimationFrame(() => requestAnimationFrame(again));
    document.fonts?.ready?.then(again).catch(() => {});
  }

  // Save a size chosen in the dialog to every list holding the item.
  async function saveChosenSizes(chosen) {
    let lists = [];
    try { lists = Q().lists() || []; } catch { /* none */ }
    for (const [from, to] of chosen) {
      const holder = lists.find((l) => (l.items || []).some((i) => i.variant_gid === from));
      // eslint-disable-next-line no-await-in-loop
      if (holder) await Q().swapVariant({ list: holder.share_token, from, to });
    }
  }
  // The entry (with product handle and title) for a card's saved variant.
  function entryFor(variantGid) {
    let items = [];
    try { items = Q().items() || []; } catch { /* none */ }
    const found = items.find((i) => i.variant_gid === variantGid);
    return found ? { ...found, variant_chosen: found.variant_chosen } : null;
  }

  // ── Actions ──────────────────────────────────────────────────

  async function removeItem(btn) {
    const li = btn.closest('li.enlist-item');
    const variantGid = li?.dataset.variantGid;
    if (!variantGid) return;
    // The list the card sits in: a list row → that list only; Favourites →
    // the pinned list, which the bridge cascades to every list, because
    // unsaved is unsaved. With nothing pinned, a plain unsave.
    const list = btn.closest('[data-enlist-list]')?.dataset.enlistList || undefined;
    btn.disabled = true;
    await Q().remove(list ? { variantGid, list } : { variantGid });
    if (document.contains(btn)) btn.disabled = false;   // a failed write rolls back
  }

  async function addFromPanel(btn) {
    const li = btn.closest('li.enlist-item');
    const list = btn.closest('[data-enlist-target]')?.dataset.enlistTarget;
    if (!li || !list) return;
    let addGid = li.dataset.variantGid;
    let addChosen = !li.hasAttribute('data-enlist-size-unchosen');
    if (!addChosen) {
      const e = entryFor(addGid);
      const chosen = e ? await chooseSizes([{ ...e, variant_chosen: false }]) : new Map();
      if (!chosen) return;                     // cancelled
      if (chosen.has(addGid)) {
        const to = chosen.get(addGid);
        await saveChosenSizes(chosen);
        addGid = to;
        addChosen = true;
      }
    }
    btn.dataset.enlistBusy = '1';
    btn.disabled = true;
    btn.textContent = attr(PANEL, 'data-enlist-added-label', 'Added');
    btn.classList.add('enlist-panel__add--added');
    const r = await Q().add({
      variantGid: addGid, productGid: li.dataset.productGid || undefined, list,
      variantChosen: addChosen,
    });
    delete btn.dataset.enlistBusy;
    if (!r?.ok) toast(r?.error || 'Something went wrong.', 'error');
    else confirmToast(`Added to ${termLower()}`);
    syncPanelButtons();
  }

  // Everything in the list that can be bought, in one request. A theme with
  // its own cart API gets it through that; otherwise the kit adds through
  // /cart/add.js and fires `enlist:cart:added` — cancel it (preventDefault) to
  // open your own cart drawer instead of going to the cart page.
  async function addListToCart(btn) {
    const token = btn.closest('[data-enlist-list-row]')?.dataset.enlistList;
    const m = model();
    const list = m.lists.find((l) => l.share_token === token);
    const entries = m.entriesOf(list);
    const chosen = await chooseSizes(entries);
    if (!chosen) return;                       // cancelled: add nothing
    const items = entries
      .filter((e) => chosen.has(e.variant_gid) || e.available_for_sale !== false)
      .map((e) => ({ id: Number(gidTail(chosen.get(e.variant_gid) || e.variant_gid)), quantity: e.qty || 1 }));
    if (!items.length) return;
    if (chosen.size) saveChosenSizes(chosen);   // remembered for next time; the cart does not wait
    btn.disabled = true;
    try {
      let cartApi = window.cart;
      if (!cartApi || typeof cartApi.add !== 'function') {
        try {
          const mod = await import('@theme/cart');
          cartApi = mod.cart || window.cart;
        } catch {}
      }
      if (cartApi && typeof cartApi.add === 'function') {
        await cartApi.add({ items });
      } else {
        const res = await fetch(`${routeRoot()}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error(String(res.status));
        const ev = new CustomEvent('enlist:cart:added', { detail: { items }, cancelable: true });
        if (document.dispatchEvent(ev)) window.location.assign(window.Shopify?.routes?.cart_url || `${routeRoot()}cart`);
      }
    } catch (err) {
      console.error('[enlist] add list to cart failed', err);
      toast('Could not add these to your cart.', 'error');
    } finally {
      btn.disabled = false;
    }
  }

  async function createList(btn) {
    const root = btn.closest('[data-enlist-create-root]') || document;
    const input = $('[data-enlist-create-name]', root);
    const name = (input?.value || '').trim() || `New ${term()}`;
    btn.disabled = true;
    const r = await Q().createList(name);
    btn.disabled = false;
    if (r?.ok) {
      if (input) input.value = '';
      toast(root.getAttribute?.('data-enlist-created-label') || 'List created.');
    } else {
      toast(r?.error || 'Something went wrong.', 'error');
    }
  }

  // ── A variant picked on a card is saved ──────────────────────
  //
  // A card that lets the shopper change size or colour keeps data-variant-id up
  // to date (the snippet contract). Watched as an attribute, so the kit does
  // not need to know which of the card's controls changed it. Enlist cascades
  // the swap to every list holding the variant: a size is about the product.
  function watchVariantChanges(scope) {
    if (!scope || scope.dataset.enlistWatch === '1') return;
    scope.dataset.enlistWatch = '1';
    new MutationObserver((records) => {
      for (const rec of records) {
        const el = rec.target;
        if (!(el instanceof Element)) continue;
        const li = el.closest('li.enlist-item');
        if (!li) continue;
        const from = li.dataset.variantGid;
        const toId = el.getAttribute('data-variant-id');
        if (!from || !toId) continue;
        const to = variantGidOf(toId);
        if (to === from && !li.hasAttribute('data-enlist-size-unchosen')) continue;
        li.removeAttribute('data-enlist-size-unchosen');
        let list = li.closest('[data-enlist-list]')?.dataset.enlistList;
        if (!list) {
          try { list = (Q().lists() || []).find((l) => (l.items || []).some((i) => i.variant_gid === from))?.share_token; } catch { /* none */ }
        }
        if (!list) continue;
        li.dataset.variantGid = to;
        const productId = el.getAttribute('data-product-id') || gidTail(li.dataset.productGid);
        Q().swapVariant({ list, from, to, productGid: productId || undefined });
        markSizes(li.parentElement);
      }
    }).observe(scope, { subtree: true, attributes: true, attributeFilter: ['data-variant-id'] });
  }

  // ── Render loop ──────────────────────────────────────────────
  //
  // qfl:wishlist:changed fires in bursts — an optimistic write, then the
  // server's answer — so renders are coalesced to one per frame.
  let scheduled = false;
  function render() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(async () => {
      scheduled = false;
      const m = model();
      await Promise.all([renderFavourites(m), renderLists(m)]);
      if (panelState) await renderPanel();
    });
  }

  function wire() {
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (sampleMode && t.closest(SAMPLE_BLOCKED)) {
        e.preventDefault();
        toast(attr(LISTS, 'data-enlist-sample-label', 'Sample content in the theme editor. Nothing is saved.'));
        return;
      }
      const bubble = t.closest('[data-enlist-bubble]');
      if (bubble) {
        e.preventDefault();
        const list = bubble.closest('[data-enlist-list-row]')?.dataset.enlistList || '';
        openCategory(bubble.dataset.enlistBubble, bubble.dataset.enlistTitle || '', list)
          .then((ok) => { if (!ok) window.location.assign(bubble.getAttribute('href')); });
        return;
      }
      const remove = t.closest('[data-enlist-remove]');
      if (remove) { e.preventDefault(); removeItem(remove); return; }
      const pick = t.closest('[data-enlist-pick]');
      if (pick) { e.preventDefault(); e.stopPropagation(); openPicker(pick.closest('li.enlist-item'), pick, e.detail === 0); return; }
      const panelAdd = t.closest('[data-enlist-panel-add]');
      if (panelAdd) { e.preventDefault(); addFromPanel(panelAdd); return; }
      const addAll = t.closest('[data-enlist-add-all]');
      if (addAll) { e.preventDefault(); addListToCart(addAll); return; }
      const create = t.closest('[data-enlist-create]');
      if (create) { e.preventDefault(); createList(create); return; }
      const edit = t.closest('[data-enlist-list-edit]');
      if (edit) { e.preventDefault(); openEdit(edit.closest('[data-enlist-list-row]')); return; }
      const dialog = t.closest('[data-enlist-edit-dialog]');
      if (dialog) {
        if (t.closest('[data-enlist-edit-cancel]')) { dialog.close(); return; }
        if (t.closest('[data-enlist-edit-delete]')) {
          // data-enlist-delete-confirm="false" on the dialog: delete at once.
          if (dialog.getAttribute('data-enlist-delete-confirm') === 'false') { deleteList(dialog); return; }
          $('[data-enlist-edit-confirm]', dialog).hidden = false;
          return;
        }
        if (t.closest('[data-enlist-edit-confirm-no]')) { $('[data-enlist-edit-confirm]', dialog).hidden = true; return; }
        if (t.closest('[data-enlist-edit-confirm-yes]')) { deleteList(dialog); return; }
      }
      if (t.closest('[data-enlist-panel-close]')) closePanel();
    });
    document.addEventListener('submit', (e) => {
      const form = e.target instanceof Element && e.target.closest('[data-enlist-edit-form]');
      if (!form) return;
      e.preventDefault();
      saveEdit(form.closest('[data-enlist-edit-dialog]'));
    });
    document.addEventListener('change', (e) => {
      const sel = e.target instanceof Element && e.target.closest('[data-enlist-sort]');
      if (!sel) return;
      favSort = sel.value || 'newness';
      render();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && $(PANEL)?.classList.contains('enlist-panel--open')) { closePanel(); return; }
      if (e.key !== 'Enter' || !(e.target instanceof Element) || !e.target.matches('[data-enlist-create-name]')) return;
      const btn = $('[data-enlist-create]', e.target.closest('[data-enlist-create-root]') || document);
      if (btn) { e.preventDefault(); createList(btn); }
    });
    document.addEventListener('qfl:wishlist:changed', render);
    // The theme editor re-renders a section when a setting changes: the rows
    // built from the old template are stale, and the new markup is empty.
    document.addEventListener('shopify:section:load', () => {
      rows.clear();
      [$(FAV), $(LISTS), $(PANEL)].forEach(watchVariantChanges);
      render();
    });
    [$(FAV), $(LISTS), $(PANEL)].forEach(watchVariantChanges);
  }

  let started = false;
  async function start() {
    if (started) return;
    // The bridge methods a lists page needs arrived together; without them
    // (an old Enlist) the page would half-work, so say so and stop. An
    // Enlist from before window.qfl.enlist has only window.QFL.
    if (!Q()) {
      if (window.QFL) console.warn('[enlist] this lists page needs a newer Enlist (window.qfl.enlist missing).');
      return;
    }
    if (typeof Q().createList !== 'function') {
      console.warn('[enlist] this lists page needs a newer Enlist (qfl.enlist.createList missing).');
      return;
    }
    started = true;
    try { await Q().ready; } catch { return; }
    wire();
    render();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('qfl:ready', start);   // the embed may boot after this file
})();
